import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { money } from "@/components/marketplace/CartDrawer";
import { productImage } from "@/lib/imageUrl";
import { toast } from "sonner";
import { Lock, ArrowLeft, ShoppingCart } from "lucide-react";

const CA_PROV = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

export default function CheckoutPage() {
  const { items, quote, discountCode, setCountry, setProvince, shippingMethod, setShippingMethod } = useCart();
  const [form, setForm] = useState({ email: "", customer_name: "", phone: "", line1: "", line2: "", city: "", region: "", postal_code: "", country: "CA" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // keep the cart quote in sync with the address the customer enters
  useEffect(() => { setCountry(form.country); }, [form.country, setCountry]);
  useEffect(() => { setProvince(form.region); }, [form.region, setProvince]);

  const opts = quote?.shipping?.options || [];
  const method = quote?.shipping?.method;
  const needsAddress = method === "standard";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Please enter your email."); return; }
    if (needsAddress && !form.line1) { toast.error("A shipping address is required for delivery."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/marketplace/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })),
        discount_code: discountCode || undefined,
        shipping_method: method,
        email: form.email, customer_name: form.customer_name, phone: form.phone,
        shipping_address: { name: form.customer_name, line1: form.line1, line2: form.line2, city: form.city, region: form.region, postal_code: form.postal_code, country: form.country, phone: form.phone },
        origin_url: window.location.origin,
      });
      window.location.href = data.url; // → Stripe Checkout
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) toast.error(typeof detail === "object" ? detail.message : "Your cart changed — please review it.");
      else toast.error(formatApiError(detail) || "Could not start checkout.");
      setSubmitting(false);
    }
  };

  if (!items.length) {
    return (
      <PublicLayout>
        <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="checkout-empty">
          <ShoppingCart className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-semibold text-slate-600">Your cart is empty.</p>
          <Link to="/marketplace/products" className="text-[#E63946] text-sm mt-2 inline-block">Shop products</Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="checkout-page">
        <Link to="/marketplace/cart" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#E63946]"><ArrowLeft className="w-4 h-4" /> Back to cart</Link>
        <h1 className="font-display text-3xl uppercase tracking-tight mt-3">Checkout</h1>
        <div className="mt-6 grid lg:grid-cols-[1.4fr_1fr] gap-8">
          {/* Form */}
          <form onSubmit={submit} className="space-y-6">
            <section>
              <div className="label-overline text-[10px] mb-2">Contact</div>
              <Input testid="co-email" type="email" placeholder="Email address" value={form.email} onChange={(v) => set("email", v)} required />
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                <Input testid="co-name" placeholder="Full name" value={form.customer_name} onChange={(v) => set("customer_name", v)} />
                <Input testid="co-phone" placeholder="Phone (optional)" value={form.phone} onChange={(v) => set("phone", v)} />
              </div>
            </section>

            <section>
              <div className="label-overline text-[10px] mb-2">Delivery</div>
              <div className="flex gap-2 mb-2">
                <select data-testid="co-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="border border-slate-300 rounded-sm px-2 py-2 text-sm bg-white">
                  <option value="CA">Canada</option>
                  <option value="US">United States</option>
                </select>
                {opts.length > 0 && (
                  <select data-testid="co-shipping-method" value={method || ""} onChange={(e) => setShippingMethod(e.target.value)} className="border border-slate-300 rounded-sm px-2 py-2 text-sm bg-white flex-1">
                    {opts.map((o) => <option key={o.id} value={o.id}>{o.label} — {o.free ? "Free" : money(o.amount_cents)}</option>)}
                  </select>
                )}
              </div>
              {needsAddress && (
                <div className="space-y-2" data-testid="co-address">
                  <Input testid="co-line1" placeholder="Address" value={form.line1} onChange={(v) => set("line1", v)} />
                  <Input testid="co-line2" placeholder="Apt, suite (optional)" value={form.line2} onChange={(v) => set("line2", v)} />
                  <div className="grid sm:grid-cols-3 gap-2">
                    <Input testid="co-city" placeholder="City" value={form.city} onChange={(v) => set("city", v)} />
                    {form.country === "CA"
                      ? <select data-testid="co-region" value={form.region} onChange={(e) => set("region", e.target.value)} className="border border-slate-300 rounded-sm px-2 py-2 text-sm bg-white"><option value="">Province</option>{CA_PROV.map((p) => <option key={p} value={p}>{p}</option>)}</select>
                      : <Input testid="co-region" placeholder="State" value={form.region} onChange={(v) => set("region", v)} />}
                    <Input testid="co-postal" placeholder="Postal code" value={form.postal_code} onChange={(v) => set("postal_code", v)} />
                  </div>
                </div>
              )}
              {method === "pickup" && <div className="text-sm text-slate-500" data-testid="co-pickup-note">You've selected local pickup — we'll email you when your order is ready.</div>}
            </section>

            <button type="submit" data-testid="place-order" disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#E63946] hover:bg-[#c62f3b] disabled:opacity-60 text-white py-3.5 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors">
              <Lock className="w-4 h-4" /> {submitting ? "Redirecting…" : `Pay ${money(quote?.total_cents)}`}
            </button>
            <p className="text-[11px] text-slate-400 text-center">Secure payment via Stripe. You'll be redirected to complete your purchase.</p>
          </form>

          {/* Summary */}
          <aside className="bg-slate-50 border border-slate-200 rounded-sm p-5 h-max" data-testid="checkout-summary">
            <div className="font-display uppercase tracking-tight mb-3">Order Summary</div>
            <div className="space-y-3">
              {(quote?.lines || []).map((l) => (
                <div key={`${l.product_id}::${l.variant_id}`} className="flex gap-3 text-sm">
                  <div className="w-12 h-12 shrink-0 bg-white rounded-sm overflow-hidden border border-slate-200">
                    <img src={productImage(l.image_url)} alt={l.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#0A192F] leading-tight">{l.name}</div>
                    <div className="text-xs text-slate-500">{Object.values(l.option_values || {}).join(" · ")} · Qty {l.qty}</div>
                  </div>
                  <div className="font-data">{money(l.line_total_cents)}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-200 mt-4 pt-3 space-y-1.5 text-sm">
              <Line label="Subtotal" value={money(quote?.subtotal_cents)} />
              {quote?.discount_cents > 0 && <Line label={`Discount (${quote?.discount?.code})`} value={`− ${money(quote.discount_cents)}`} tone="text-emerald-600" />}
              <Line label={`Shipping${quote?.shipping?.label ? ` · ${quote.shipping.label}` : ""}`} value={quote?.shipping_cents ? money(quote.shipping_cents) : "Free"} />
              {quote?.tax?.enabled && quote?.tax_cents > 0 && <Line label={`Tax (${quote?.tax?.name})`} value={money(quote.tax_cents)} />}
              <div className="flex justify-between pt-2 border-t border-slate-200 font-semibold text-[#0A192F]"><span>Total</span><span data-testid="co-total" className="font-data text-lg">{money(quote?.total_cents)}</span></div>
            </div>
          </aside>
        </div>
      </div>
    </PublicLayout>
  );
}

function Input({ testid, type = "text", placeholder, value, onChange, required }) {
  return <input data-testid={testid} type={type} placeholder={placeholder} value={value} required={required}
    onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm outline-none focus:border-[#0A192F]" />;
}
function Line({ label, value, tone = "text-slate-600" }) {
  return <div className="flex justify-between"><span className={tone}>{label}</span><span className={`font-data ${tone}`}>{value}</span></div>;
}
